/**
 * The instance half of the dual channel event system.
 *
 * `emit` drives both channels from one call: it runs the instance listeners and
 * dispatches the DOM `CustomEvent`. Neither channel short circuits the other,
 * so a consumer cannot tell which one another consumer used, and cancelling
 * from either has exactly the same effect.
 */

import { CANCELABLE, dispatchDom } from './DomEvents'
import type { EventName, EventPayload } from './types'

/** The payload an instance listener receives, with a DOM-like `preventDefault`. */
export interface EmitterEvent extends EventPayload {
  /** Cancels the event. A no-op unless the event is in `CANCELABLE`. */
  preventDefault(): void
}

export type Listener = (payload: EmitterEvent) => void

export class Emitter {
  readonly #listeners = new Map<EventName, Listener[]>()

  /**
   * Subscribe to an event. Returns an unsubscribe function that is safe to
   * call more than once and will never remove a later registration of the same
   * function.
   */
  on(name: EventName, fn: Listener): () => void {
    const list = this.#listeners.get(name)

    if (list === undefined) {
      this.#listeners.set(name, [fn])
    } else if (!list.includes(fn)) {
      list.push(fn)
    }

    let disposed = false

    return () => {
      if (disposed) return

      disposed = true
      this.off(name, fn)
    }
  }

  /** Unsubscribe a listener. Unknown listeners are ignored. */
  off(name: EventName, fn: Listener): void {
    const list = this.#listeners.get(name)

    if (list === undefined) return

    const index = list.indexOf(fn)

    if (index !== -1) list.splice(index, 1)
  }

  /**
   * Run both channels for one event.
   *
   * Returns `false` only when the event is cancelable and was cancelled, on
   * either channel. The DOM event is dispatched only when a `target` is given.
   */
  emit(name: EventName, payload: EventPayload, target?: HTMLElement): boolean {
    const cancelable = CANCELABLE.has(name)
    let cancelled = false

    // A shallow copy, so the `preventDefault` handle never leaks onto the
    // object the DOM channel exposes as `detail`. Every field is copied by
    // reference, so `items` is still the array the caller is about to render.
    const event: EmitterEvent = {
      ...payload,
      preventDefault: () => {
        if (cancelable) cancelled = true
      },
    }

    // Snapshot before iterating: a listener that subscribes during an emit must
    // not run in that same emit, and a listener that unsubscribes during it
    // must not run at all. The live membership check below covers the removal.
    const list = this.#listeners.get(name)
    const snapshot = list === undefined ? [] : [...list]

    for (const fn of snapshot) {
      if (list !== undefined && !list.includes(fn)) continue

      try {
        fn(event)
      } catch (error) {
        // One bad listener must not silence the rest, and must not be mistaken
        // for a cancellation.
        console.error(`[context-menu] a "${name}" listener threw`, error)
      }
    }

    const domAllowed = target === undefined ? true : dispatchDom(target, name, payload)

    return !cancelled && domAllowed
  }
}
